import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { CheckCircle, ArrowRight } from "@phosphor-icons/react";

const WEBINAR_OPTIONS = [
  "Leadership and School Growth", "Career Growth and Professional Development",
  "Online Tutoring and Digital Opportunities", "Money, Sustainability and Income Growth",
  "Technology and Innovation",
];
const RESOURCE_OPTIONS = [
  "School and Administrative Resources", "Teaching and Tutoring Resources",
  "Leadership and Career Resources", "Business and Financial Resources",
];
// Hard-coded fallback so the dropdown is never empty if /pod/meta is slow / fails.
const FALLBACK_CATEGORIES = [
  "Proprietors", "School Owners", "Principals", "Vice Principals",
  "Head Teachers", "Teachers", "Subject Specialists", "Administrators",
  "ICT Personnel", "Guidance Counsellors",
];

export default function PodSignupPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({
    full_name: "", email: "", password: "", confirm_password: "", phone: "", country: "", state: "", city: "",
    employer_name: "", school_name: "", job_title: "", job_function: "", department: "",
    professional_category: "", years_of_experience: "",
  });
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [interests, setInterests] = useState({ webinars: [], resources: [], networking: "", devel: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get("/pod/meta").then(r => {
      const list = r.data?.professional_categories || [];
      if (Array.isArray(list) && list.length > 0) setCategories(list);
    }).catch(() => {});
  }, []);

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleArr = (arr, item) => arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item];

  const submit = async () => {
    if (!form.full_name || !form.email) return toast.error("Name and email are required");
    if (!form.password || form.password.length < 6) return toast.error("Password must be at least 6 characters");
    if (form.password !== form.confirm_password) return toast.error("Passwords do not match");
    if (!form.professional_category) return toast.error("Please pick your professional category");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        years_of_experience: form.years_of_experience ? parseInt(form.years_of_experience, 10) : null,
        webinar_interests: interests.webinars,
        resource_interests: interests.resources,
        networking_interests: interests.networking ? interests.networking.split(",").map(s => s.trim()).filter(Boolean) : [],
        professional_development_interests: interests.devel ? interests.devel.split(",").map(s => s.trim()).filter(Boolean) : [],
      };
      delete payload.confirm_password;
      const { data } = await api.post("/pod/signup", payload);
      // Auto-login the brand-new subscriber and bounce them into the POD area.
      const loginRes = await login(form.email, form.password);
      if (loginRes.success) {
        toast.success(`Welcome to the POD! You've been added to ${data.roundtable}.`);
        navigate("/pod/dashboard");
      } else {
        setDone(data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Signup failed");
    }
    setSubmitting(false);
  };

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="pod-signup-success">
        <div className="max-w-lg bg-white border border-slate-200 p-8 text-center">
          <CheckCircle size={56} weight="fill" className="text-emerald-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2 text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>Welcome to the POD!</h1>
          <p className="text-sm text-slate-600 mb-3">You've been added to the <b>{done.roundtable}</b> Roundtable.</p>
          <p className="text-xs text-slate-500 mb-6">We'll send curated webinars, resources, and networking suggestions to <b>{form.email}</b>.</p>
          <div className="flex gap-2 justify-center">
            <Link to="/pod"><Button className="bg-blue-800 text-white hover:bg-blue-900 rounded-none">Explore POD</Button></Link>
            <Link to="/login"><Button variant="outline" className="rounded-none">Sign In</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12" data-testid="pod-signup-page">
      <div className="max-w-3xl mx-auto bg-white border border-slate-200 p-6 sm:p-10">
        <p className="text-xs uppercase tracking-[0.25em] text-amber-600 font-bold mb-1">SBL Leadership POD</p>
        <h1 className="text-3xl font-bold mb-2 text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>Join the POD</h1>
        <p className="text-sm text-slate-600 mb-8">Tell us about your role so we can match you to the right Roundtable and curate webinars and resources just for you.</p>

        <h2 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">Personal Details</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          <div><Label>Full Name *</Label><Input value={form.full_name} onChange={e => update("full_name", e.target.value)} className="rounded-none" data-testid="signup-full-name" /></div>
          <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => update("email", e.target.value)} className="rounded-none" data-testid="signup-email" /></div>
          <div><Label>Password *</Label><Input type="password" value={form.password} onChange={e => update("password", e.target.value)} className="rounded-none" data-testid="signup-password" placeholder="Min 6 characters" /></div>
          <div><Label>Confirm Password *</Label><Input type="password" value={form.confirm_password} onChange={e => update("confirm_password", e.target.value)} className="rounded-none" data-testid="signup-confirm-password" /></div>
          <div><Label>Phone Number</Label><Input value={form.phone} onChange={e => update("phone", e.target.value)} className="rounded-none" /></div>
          <div><Label>Country</Label><Input value={form.country} onChange={e => update("country", e.target.value)} className="rounded-none" /></div>
          <div><Label>State / Region</Label><Input value={form.state} onChange={e => update("state", e.target.value)} className="rounded-none" /></div>
          <div><Label>City</Label><Input value={form.city} onChange={e => update("city", e.target.value)} className="rounded-none" /></div>
        </div>

        <h2 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">Professional Details</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          <div><Label>Employer Name</Label><Input value={form.employer_name} onChange={e => update("employer_name", e.target.value)} className="rounded-none" data-testid="signup-employer" /></div>
          <div><Label>School Name</Label><Input value={form.school_name} onChange={e => update("school_name", e.target.value)} className="rounded-none" data-testid="signup-school" /></div>
          <div><Label>Job Title</Label><Input value={form.job_title} onChange={e => update("job_title", e.target.value)} className="rounded-none" /></div>
          <div><Label>Job Function</Label><Input value={form.job_function} onChange={e => update("job_function", e.target.value)} className="rounded-none" /></div>
          <div><Label>Department</Label><Input value={form.department} onChange={e => update("department", e.target.value)} className="rounded-none" /></div>
          <div>
            <Label>Professional Category *</Label>
            <Select value={form.professional_category} onValueChange={v => update("professional_category", v)}>
              <SelectTrigger className="rounded-none" data-testid="signup-prof-category"><SelectValue placeholder="Pick a category" /></SelectTrigger>
              <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Years of Experience</Label><Input type="number" value={form.years_of_experience} onChange={e => update("years_of_experience", e.target.value)} className="rounded-none" /></div>
        </div>

        <h2 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">Interests</h2>
        <p className="text-xs text-slate-500 mb-3">Pick the webinar topics and resource areas that matter most to you.</p>
        <div className="mb-3">
          <Label className="block mb-2">Webinar Interests</Label>
          <div className="flex gap-2 flex-wrap">
            {WEBINAR_OPTIONS.map(o => (
              <button key={o} type="button" onClick={() => setInterests(s => ({ ...s, webinars: toggleArr(s.webinars, o) }))} className={`px-3 py-1.5 text-xs border ${interests.webinars.includes(o) ? "bg-blue-800 text-white border-blue-800" : "border-slate-300 text-slate-700 hover:border-blue-800"}`}>{o}</button>
            ))}
          </div>
        </div>
        <div className="mb-3">
          <Label className="block mb-2">Resource Interests</Label>
          <div className="flex gap-2 flex-wrap">
            {RESOURCE_OPTIONS.map(o => (
              <button key={o} type="button" onClick={() => setInterests(s => ({ ...s, resources: toggleArr(s.resources, o) }))} className={`px-3 py-1.5 text-xs border ${interests.resources.includes(o) ? "bg-blue-800 text-white border-blue-800" : "border-slate-300 text-slate-700 hover:border-blue-800"}`}>{o}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
          <div><Label>Networking interests (comma-separated)</Label><Input value={interests.networking} onChange={e => setInterests(s => ({ ...s, networking: e.target.value }))} className="rounded-none" /></div>
          <div><Label>Professional development goals (comma-separated)</Label><Input value={interests.devel} onChange={e => setInterests(s => ({ ...s, devel: e.target.value }))} className="rounded-none" /></div>
        </div>

        <Button disabled={submitting} onClick={submit} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none w-full py-6 text-base gap-2" data-testid="pod-signup-submit">
          {submitting ? "Joining…" : <>Join the POD <ArrowRight weight="bold" size={16} /></>}
        </Button>
      </div>
    </div>
  );
}
